import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    PawPrint,
    Truck,
    PackagePlus,
    Building2,
    HandHeart,
    Utensils,
    AlertTriangle,
    MapPin,
} from "lucide-react";
import { fmtTimeAgo } from "../lib/format";
import { toast } from "sonner";

const URGENCY_COLORS = {
    critical: "bg-[var(--rl-urgent)] text-white",
    high: "bg-[#f2b787] text-[#5b3411]",
    medium: "bg-[var(--rl-cream)] text-[var(--rl-text-2)]",
    low: "bg-[var(--rl-sage)] text-[var(--rl-text)]",
};

function TabHeader({ active, onSelect }) {
    return (
        <div className="inline-flex rounded-full bg-white border border-[var(--rl-border)] p-1 shadow-sm" data-testid="pet-food-tabs">
            <button
                onClick={() => onSelect("donate")}
                data-testid="tab-donate"
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${active === "donate" ? "bg-[#8b4a1c] text-white" : "text-[var(--rl-text-2)]"}`}
            >
                Donate food
            </button>
            <button
                onClick={() => onSelect("receive")}
                data-testid="tab-receive"
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${active === "receive" ? "bg-[#8b4a1c] text-white" : "text-[var(--rl-text-2)]"}`}
            >
                Request food
            </button>
        </div>
    );
}

function DonateForm({ onSubmitted }) {
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        donor_name: "", donor_type: "individual", email: "", phone: "",
        method: "drop_off", brand: "", food_type: "mixed",
        quantity_lbs: "", location: "", country: "USA", message: "",
    });
    const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });
    const setV = (k) => (v) => setForm({ ...form, [k]: v });

    async function submit(e) {
        e.preventDefault();
        if (!form.donor_name.trim()) return toast.error("Add your name");
        setBusy(true);
        try {
            const body = {
                ...form,
                quantity_lbs: form.quantity_lbs ? parseFloat(form.quantity_lbs) : null,
                email: form.email || null,
                phone: form.phone || null,
                brand: form.brand || null,
                location: form.location || null,
                message: form.message || null,
            };
            const { data } = await api.post("/food/pledge", body);
            toast.success("Thanks — your pledge is live in the network.");
            onSubmitted?.(data);
            setForm({ ...form, donor_name: "", email: "", phone: "", quantity_lbs: "", message: "", location: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not save pledge");
        }
        setBusy(false);
    }

    return (
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="donate-form">
            <Field label="Your name / org">
                <Input value={form.donor_name} onChange={set("donor_name")} data-testid="donor-name" />
            </Field>
            <Field label="I'm a…">
                <Select value={form.donor_type} onValueChange={setV("donor_type")}>
                    <SelectTrigger data-testid="donor-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="individual">Individual</SelectItem>
                        <SelectItem value="business">Local business</SelectItem>
                        <SelectItem value="brand">Pet-food brand</SelectItem>
                        <SelectItem value="food_bank">Food bank / rescue</SelectItem>
                    </SelectContent>
                </Select>
            </Field>
            <Field label="Email (for confirmation)">
                <Input type="email" value={form.email} onChange={set("email")} data-testid="donor-email" />
            </Field>
            <Field label="Phone (optional)">
                <Input value={form.phone} onChange={set("phone")} data-testid="donor-phone" />
            </Field>
            <Field label="How will you get it there?">
                <Select value={form.method} onValueChange={setV("method")}>
                    <SelectTrigger data-testid="donor-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="drop_off">Drop off at a partner</SelectItem>
                        <SelectItem value="shipping">Ship directly</SelectItem>
                        <SelectItem value="bulk_pickup">Bulk pickup (I have a truck)</SelectItem>
                        <SelectItem value="brand_partnership">Brand partnership / recurring</SelectItem>
                    </SelectContent>
                </Select>
            </Field>
            <Field label="Food type">
                <Select value={form.food_type} onValueChange={setV("food_type")}>
                    <SelectTrigger data-testid="donor-food-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="dry">Dry / kibble</SelectItem>
                        <SelectItem value="wet">Wet / canned</SelectItem>
                        <SelectItem value="puppy">Puppy formula</SelectItem>
                        <SelectItem value="senior">Senior</SelectItem>
                        <SelectItem value="prescription">Prescription / medical</SelectItem>
                        <SelectItem value="mixed">Mixed / assorted</SelectItem>
                    </SelectContent>
                </Select>
            </Field>
            <Field label="Approx. pounds">
                <Input type="number" min="0" value={form.quantity_lbs} onChange={set("quantity_lbs")} data-testid="donor-lbs" />
            </Field>
            <Field label="Your location (city, region)">
                <Input value={form.location} onChange={set("location")} data-testid="donor-location" />
            </Field>
            {(form.donor_type === "brand" || form.method === "brand_partnership") && (
                <Field label="Brand name" className="md:col-span-2">
                    <Input value={form.brand} onChange={set("brand")} data-testid="donor-brand" />
                </Field>
            )}
            <Field label="A note to the rescue (optional)" className="md:col-span-2">
                <Textarea value={form.message} onChange={set("message")} data-testid="donor-message" />
            </Field>
            <div className="md:col-span-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-[var(--rl-text-3)] max-w-md">
                    We&apos;ll match your pledge to a rescue within 24 hours and email you confirmation + logistics.
                </div>
                <Button type="submit" disabled={busy} className="rounded-full h-12 px-6 bg-[#8b4a1c] hover:bg-[#6f3811] text-white" data-testid="donor-submit">
                    <PawPrint className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Pledge pet food"}
                </Button>
            </div>
        </form>
    );
}

function RequestForm({ onSubmitted }) {
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        rescue_name: "", email: "", phone: "", location: "", country: "USA",
        urgency: "medium", food_type: "mixed", quantity_needed_lbs: "",
        dogs_served: "", story: "",
    });
    const set = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });
    const setV = (k) => (v) => setForm({ ...form, [k]: v });

    async function submit(e) {
        e.preventDefault();
        if (!form.rescue_name.trim()) return toast.error("Add your rescue's name");
        const lbs = parseFloat(form.quantity_needed_lbs);
        if (!lbs || lbs <= 0) return toast.error("Add pounds needed");
        setBusy(true);
        try {
            const body = {
                ...form,
                quantity_needed_lbs: lbs,
                dogs_served: form.dogs_served ? parseInt(form.dogs_served, 10) : null,
                email: form.email || null,
                phone: form.phone || null,
                story: form.story || null,
            };
            const { data } = await api.post("/food/request", body);
            toast.success("Your food request is live — donors are being notified.");
            onSubmitted?.(data);
            setForm({ ...form, rescue_name: "", email: "", phone: "", quantity_needed_lbs: "", dogs_served: "", story: "" });
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not post request");
        }
        setBusy(false);
    }

    return (
        <form onSubmit={submit} className="grid md:grid-cols-2 gap-4" data-testid="request-form">
            <Field label="Rescue / shelter name">
                <Input value={form.rescue_name} onChange={set("rescue_name")} data-testid="rescue-name" />
            </Field>
            <Field label="Urgency">
                <Select value={form.urgency} onValueChange={setV("urgency")}>
                    <SelectTrigger data-testid="rescue-urgency"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="low">Low · planning ahead</SelectItem>
                        <SelectItem value="medium">Medium · &lt;30 days supply</SelectItem>
                        <SelectItem value="high">High · &lt;14 days supply</SelectItem>
                        <SelectItem value="critical">Critical · &lt;7 days / out</SelectItem>
                    </SelectContent>
                </Select>
            </Field>
            <Field label="Contact email">
                <Input type="email" value={form.email} onChange={set("email")} data-testid="rescue-email" />
            </Field>
            <Field label="Phone (optional)">
                <Input value={form.phone} onChange={set("phone")} data-testid="rescue-phone" />
            </Field>
            <Field label="Location (city, region)">
                <Input value={form.location} onChange={set("location")} data-testid="rescue-location" />
            </Field>
            <Field label="Country">
                <Input value={form.country} onChange={set("country")} data-testid="rescue-country" />
            </Field>
            <Field label="Food type needed">
                <Select value={form.food_type} onValueChange={setV("food_type")}>
                    <SelectTrigger data-testid="rescue-food-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="dry">Dry / kibble</SelectItem>
                        <SelectItem value="wet">Wet / canned</SelectItem>
                        <SelectItem value="puppy">Puppy formula</SelectItem>
                        <SelectItem value="senior">Senior</SelectItem>
                        <SelectItem value="prescription">Prescription / medical</SelectItem>
                        <SelectItem value="mixed">Mixed / assorted</SelectItem>
                    </SelectContent>
                </Select>
            </Field>
            <Field label="Pounds needed">
                <Input type="number" min="1" value={form.quantity_needed_lbs} onChange={set("quantity_needed_lbs")} data-testid="rescue-lbs" />
            </Field>
            <Field label="Dogs served (optional)">
                <Input type="number" min="0" value={form.dogs_served} onChange={set("dogs_served")} data-testid="rescue-dogs" />
            </Field>
            <Field label="Why (context helps donors)" className="md:col-span-2">
                <Textarea value={form.story} onChange={set("story")} data-testid="rescue-story" placeholder="e.g. Just took in a litter of 12 orphaned pups — need puppy food for 6 weeks." />
            </Field>
            <div className="md:col-span-2 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-[var(--rl-text-3)] max-w-md">
                    Alerts go to individual donors, brand partners, and nearby food banks based on urgency + location.
                </div>
                <Button type="submit" disabled={busy} className="rounded-full h-12 px-6 bg-[#8b4a1c] hover:bg-[#6f3811] text-white" data-testid="rescue-submit">
                    <HandHeart className="w-4 h-4 mr-2" /> {busy ? "Posting…" : "Post food request"}
                </Button>
            </div>
        </form>
    );
}

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="eyebrow">{label}</span>
            <div className="mt-1.5">{children}</div>
        </label>
    );
}

function ChannelCard({ icon: Icon, title, body }) {
    return (
        <div className="card-warm rounded-xl p-5">
            <div className="w-10 h-10 rounded-full bg-[var(--rl-cream)] flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-[#8b4a1c]" />
            </div>
            <div className="font-serif text-xl leading-tight">{title}</div>
            <p className="text-sm text-[var(--rl-text-2)] mt-2 leading-relaxed">{body}</p>
        </div>
    );
}

export default function PetFood() {
    const [tab, setTab] = useState("donate");
    const [stats, setStats] = useState(null);
    const [pledges, setPledges] = useState([]);
    const [requests, setRequests] = useState([]);

    async function load() {
        const [s, p, r] = await Promise.all([
            api.get("/food/stats"),
            api.get("/food/pledges", { params: { limit: 12 } }),
            api.get("/food/requests", { params: { limit: 12 } }),
        ]);
        setStats(s.data);
        setPledges(p.data);
        setRequests(r.data);
    }
    useEffect(() => { load(); }, []);

    return (
        <div data-testid="pet-food-page">
            {/* Hero */}
            <section className="relative overflow-hidden bg-gradient-to-br from-[#fff2e1] via-[#fce0bd] to-[#f2b787] border-b border-[#c2724e]">
                <PawPrint className="absolute -right-12 -top-12 w-64 h-64 opacity-10 rotate-12 text-[#8b4a1c]" />
                <PawPrint className="absolute right-64 bottom-4 w-24 h-24 opacity-10 -rotate-12 text-[#8b4a1c]" />
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-12 gap-10 items-start relative">
                    <div className="lg:col-span-7 text-[#2e2015]">
                        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest font-semibold text-[#8b4a1c] mb-3">
                            <Utensils className="w-3.5 h-3.5" /> Pet Food Network
                        </div>
                        <h1 className="font-serif text-[42px] leading-[1.05] sm:text-6xl tracking-tight">
                            Every bowl matters.<br />
                            <em className="not-italic text-[#8b4a1c]">Every pound saves a rescue&apos;s budget.</em>
                        </h1>
                        <p className="mt-6 text-lg opacity-85 max-w-xl leading-relaxed">
                            Food is the largest recurring line item on any rescue&apos;s balance sheet. We connect individuals, businesses, and pet-food brands with the rescues, food banks, and shelters that need it most — with drop-off routes, ship-to-any programs, and emergency assistance.
                        </p>
                        <div className="mt-8">
                            <TabHeader active={tab} onSelect={setTab} />
                        </div>
                    </div>
                    <div className="lg:col-span-5 grid grid-cols-2 gap-3">
                        <StatTile label="lbs pledged" value={stats ? Math.round(stats.pounds_pledged).toLocaleString() : "—"} />
                        <StatTile label="lbs needed" value={stats ? Math.round(stats.pounds_needed).toLocaleString() : "—"} />
                        <StatTile label="open pledges" value={stats ? stats.pledges_total : "—"} />
                        <StatTile label="urgent asks" value={stats ? stats.urgent_open : "—"} accent={stats && stats.urgent_open > 0} />
                    </div>
                </div>
            </section>

            {/* Active tab content */}
            {tab === "donate" ? (
                <>
                    {/* Channels */}
                    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
                        <div className="eyebrow text-[#8b4a1c] mb-3">Four ways to donate food</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="donate-channels">
                            <ChannelCard icon={PackagePlus} title="Drop-off" body="Bring bags to a partner rescue or shelter within 30 miles. We route your city to the nearest intake point." />
                            <ChannelCard icon={Truck} title="Ship directly" body="Chewy, Amazon, and brand ship-to-any partners deliver straight to a matched rescue's address — no middle stop." />
                            <ChannelCard icon={Building2} title="Brand partnership" body="Pet-food brands: set up a recurring donation program with dedicated placement, tax docs, and impact reports." />
                            <ChannelCard icon={PawPrint} title="Bulk pickup" body="Have a truck or warehouse? Coordinate weekly or monthly bulk moves to under-resourced shelters." />
                        </div>
                    </section>

                    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-7">
                            <h2 className="font-serif text-3xl mb-1">Pledge pet food</h2>
                            <p className="text-sm text-[var(--rl-text-2)] mb-6">Fill this in and we&apos;ll match your donation to a rescue in the next 24 hours.</p>
                            <div className="card-warm rounded-2xl p-6 lg:p-8">
                                <DonateForm onSubmitted={load} />
                            </div>
                        </div>
                        <aside className="lg:col-span-5">
                            <h3 className="font-serif text-2xl mb-3">Recent pledges</h3>
                            <div className="space-y-2" data-testid="pledges-list">
                                {pledges.length === 0 && (
                                    <div className="card-warm rounded-lg p-5 text-center text-sm text-[var(--rl-text-2)]">Be the first to pledge food.</div>
                                )}
                                {pledges.map((p) => (
                                    <div key={p.id} className="card-warm rounded-lg p-4">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            <span className="font-semibold">{p.donor_name}</span>
                                            {p.brand && <Badge variant="outline" className="text-[10px]">{p.brand}</Badge>}
                                            <Badge className="text-[10px] bg-[#f2b787] text-[#5b3411] hover:bg-[#f2b787]">{p.donor_type.replace("_", " ")}</Badge>
                                        </div>
                                        <div className="text-xs text-[var(--rl-text-2)] mt-1">
                                            {p.quantity_lbs ? `${p.quantity_lbs.toLocaleString()} lbs` : "Quantity flexible"} · {p.food_type} · {p.method.replace(/_/g, " ")}
                                        </div>
                                        {p.location && <div className="text-xs text-[var(--rl-text-3)] mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.location}</div>}
                                        {p.message && <div className="text-xs italic text-[var(--rl-text-2)] mt-2">&ldquo;{p.message}&rdquo;</div>}
                                        <div className="text-[10px] text-[var(--rl-text-3)] mt-2">{fmtTimeAgo(p.created_at)}</div>
                                    </div>
                                ))}
                            </div>
                        </aside>
                    </section>
                </>
            ) : (
                <>
                    {/* Receive channels */}
                    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
                        <div className="eyebrow text-[#8b4a1c] mb-3">Four channels to receive food</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="receive-channels">
                            <ChannelCard icon={Utensils} title="Pet food banks" body="Regional food banks route surplus to partner rescues weekly. Free to join for verified shelters." />
                            <ChannelCard icon={Building2} title="Brand programs" body="Chewy Gives Back, Open Farm, PetSmart Charities, and 8+ others — one intake form, ongoing matches." />
                            <ChannelCard icon={HandHeart} title="Community drives" body="Local shops, corporate teams, and schools organize drives — post an ask and we'll route the drive to you." />
                            <ChannelCard icon={AlertTriangle} title="Emergency assistance" body="Under 7 days of food? Post a critical ask — donors and food banks are alerted within the hour." />
                        </div>
                    </section>

                    <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 grid lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-7">
                            <h2 className="font-serif text-3xl mb-1">Post a food request</h2>
                            <p className="text-sm text-[var(--rl-text-2)] mb-6">Every rescue on the network can post — no gatekeeping. Higher-urgency asks go out to more donors.</p>
                            <div className="card-warm rounded-2xl p-6 lg:p-8">
                                <RequestForm onSubmitted={load} />
                            </div>
                        </div>
                        <aside className="lg:col-span-5">
                            <h3 className="font-serif text-2xl mb-3">Open requests</h3>
                            <div className="space-y-2" data-testid="requests-list">
                                {requests.length === 0 && (
                                    <div className="card-warm rounded-lg p-5 text-center text-sm text-[var(--rl-text-2)]">No open food requests right now.</div>
                                )}
                                {requests.map((r) => (
                                    <div key={r.id} className="card-warm rounded-lg p-4">
                                        <div className="flex items-baseline gap-2 flex-wrap">
                                            <span className="font-semibold">{r.rescue_name}</span>
                                            <Badge className={`text-[10px] ${URGENCY_COLORS[r.urgency] || ""}`}>{r.urgency}</Badge>
                                        </div>
                                        <div className="text-xs text-[var(--rl-text-2)] mt-1">
                                            {r.quantity_needed_lbs.toLocaleString()} lbs · {r.food_type} {r.dogs_served ? `· serves ${r.dogs_served} dogs` : ""}
                                        </div>
                                        <div className="text-xs text-[var(--rl-text-3)] mt-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.location}, {r.country}</div>
                                        {r.story && <div className="text-xs italic text-[var(--rl-text-2)] mt-2">&ldquo;{r.story}&rdquo;</div>}
                                        <div className="text-[10px] text-[var(--rl-text-3)] mt-2">{fmtTimeAgo(r.created_at)}</div>
                                    </div>
                                ))}
                            </div>
                        </aside>
                    </section>
                </>
            )}
        </div>
    );
}

function StatTile({ label, value, accent }) {
    return (
        <div className="rounded-xl p-4 bg-white/70 backdrop-blur-sm border border-white/60">
            <div className={`font-serif text-4xl ${accent ? "text-[var(--rl-urgent)]" : "text-[#8b4a1c]"}`}>{value}</div>
            <div className="eyebrow mt-1 text-[#5b3411]">{label}</div>
        </div>
    );
}
